import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate, type Location } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  BarChart3,
  BookOpen,
  Building2,
  CalendarClock,
  ChevronDown,
  Compass,
  Gauge,
  House,
  Signpost,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  Library,
  LogOut,
  MessageSquareHeart,
  Moon,
  Network,
  ScrollText,
  Settings,
  IdCard,
  ShieldCheck,
  Sparkles,
  Sun,
  UserCog,
  UserPlus,
  Users,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { useApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCurrentOrg } from '@/lib/current-org';
import { useMe } from '@/lib/me';
import { isAuthor, isManager } from '@/lib/org-roles';
import { useTheme } from '@/lib/theme';
import { useDismiss } from '@/lib/use-dismiss';
import { ReadyMark } from '../logo';
import { OrgSwitcher } from './org-switcher';
import { FeedbackDialog } from '../feedback-dialog';
import { HelpDialog } from '../help/help-dialog';

/*
 * The app shell's navigation: My Learning (always personal), the current
 * organization's pages (what the user's role there allows), platform admin,
 * and the user menu. The same content fills the desktop sidebar (collapsible
 * to an icon rail) and the phone drawer.
 */

interface Item {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** When the path alone can't tell (Explore vs an org's Offerings, both on /learn). */
  match?: (location: Location) => boolean;
  /** Unread count shown beside it. */
  badge?: number;
}

/** The sidebar section the current page belongs to, if any. */
function sectionFor(pathname: string, search: string, orgId: string | null): string | null {
  const under = (p: string) => pathname === p || pathname.startsWith(`${p}/`);
  if (under('/admin')) return 'admin';
  if (under('/organizations')) return 'org';
  // An org's Offerings is Explore filtered to that org.
  if (under('/learn') && orgId && new URLSearchParams(search).get('source') === orgId) return 'org';
  if (['/home', '/learn', '/paths', '/certificates', '/help', '/o'].some(under)) return 'learning';
  return null;
}

/**
 * One section open at a time (an accordion), so the list stays short. Every
 * navigation opens the section of the page it lands on and closes the rest;
 * pages that belong to none (Account) leave it as it was. Opening a section
 * by hand closes the others too.
 */
function useOpenSection() {
  const location = useLocation();
  const { current } = useCurrentOrg();
  const routeSection = sectionFor(location.pathname, location.search, current?.id ?? null);
  const [open, setOpen] = useState<string | null>(routeSection ?? 'learning');
  useEffect(() => {
    if (routeSection) setOpen(routeSection);
  }, [location.key, routeSection]);
  return {
    isOpen: (id: string) => open === id,
    toggle: (id: string) => setOpen((o) => (o === id ? null : id)),
  };
}

function NavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: Item;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  const location = useLocation();
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex min-h-10 items-center gap-3 rounded-lg text-sm font-medium transition-colors ${collapsed ? 'justify-center px-2' : 'px-3'} ${(item.match ? item.match(location) : isActive) ? 'bg-primary/15 text-primary-strong' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`
      }
    >
      <span className="relative shrink-0">
        <Icon className="size-5" aria-hidden />
        {collapsed && Boolean(item.badge) && (
          <span className="absolute -right-1 -top-1 size-2.5 rounded-full bg-primary" />
        )}
      </span>
      {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
      {!collapsed && Boolean(item.badge) && (
        <span className="rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground tabular-nums">
          {item.badge! > 99 ? '99+' : item.badge}
          <span className="sr-only"> unread</span>
        </span>
      )}
    </NavLink>
  );
}

function Section({
  id,
  title,
  items,
  collapsed,
  sections,
  onNavigate,
}: {
  id: string;
  title: string;
  items: Item[];
  collapsed: boolean;
  sections: ReturnType<typeof useOpenSection>;
  onNavigate?: () => void;
}) {
  if (!items.length) return null;
  const open = collapsed || sections.isOpen(id);
  return (
    <div className="space-y-0.5">
      {collapsed ? (
        <div className="mx-2 my-2 border-t border-border" />
      ) : (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => sections.toggle(id)}
          className="flex min-h-9 w-full items-center justify-between rounded-md px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
        >
          <span className="truncate">{title}</span>
          <ChevronDown
            className={`size-4 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`}
            aria-hidden
          />
        </button>
      )}
      {open &&
        items.map((i) => (
          <NavItem key={i.to} item={i} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
    </div>
  );
}

const fromSource = (l: Location) => new URLSearchParams(l.search).get('source');

const LEARNING: Item[] = [
  { to: '/home', label: 'Home', icon: House },
  {
    to: '/learn',
    label: 'Explore',
    icon: Compass,
    match: (l) => l.pathname.startsWith('/learn') && !fromSource(l),
  },
  { to: '/paths', label: 'Learning paths', icon: Signpost },
  { to: '/certificates', label: 'Certificates', icon: Award },
  { to: '/o', label: 'Organizations', icon: Building2 },
];

const ADMIN: Item[] = [
  { to: '/admin', label: 'Overview', icon: Gauge, end: true },
  { to: '/admin/feedback', label: 'Feedback', icon: MessageSquareHeart },
  { to: '/admin/help', label: 'Helpdesk', icon: LifeBuoy },
  { to: '/admin/requests', label: 'Requests', icon: Inbox },
  { to: '/admin/organizations', label: 'Organizations', icon: Building2 },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/library', label: 'Library', icon: Library },
  { to: '/admin/reports', label: 'Reports', icon: BarChart3 },
  { to: '/admin/audit', label: 'Audit log', icon: ScrollText },
];

/** Unread items in the inboxes this user looks after; refreshed every few minutes. */
function useInboxCounts() {
  const api = useApi();
  const { current } = useCurrentOrg();
  return useQuery({
    queryKey: ['inboxCounts', current?.id ?? null],
    queryFn: () => api.inboxCounts(current?.id),
    refetchInterval: 5 * 60_000,
  });
}

/** The current org's pages, by what the user's role there allows (the pages check again). */
function useOrgItems(): { title: string; items: Item[] } | null {
  const api = useApi();
  const { current } = useCurrentOrg();
  const role = current?.role ?? null;
  const canManage = isManager(role);
  // Admins of this org, or (no role here) admins of a parent org viewing it.
  const isAdminView = Boolean(current) && (canManage || role === null);
  const usage = useQuery({
    queryKey: ['usage', current?.id],
    queryFn: () => api.usage(current!.id),
    enabled: isAdminView,
  });
  const counts = useInboxCounts();
  if (!current) return null;
  const base = `/organizations/${current.id}`;
  const showChildren =
    isAdminView &&
    current.depth < 2 &&
    Boolean(
      usage.data && (usage.data.limits.maxChildOrgs > 0 || usage.data.usage.maxChildOrgs > 0),
    );
  const items: (Item & { show: boolean })[] = [
    { to: base, label: 'Overview', icon: LayoutDashboard, end: true, show: true },
    {
      to: `/learn?source=${current.id}`,
      label: 'Offerings',
      icon: Sparkles,
      match: (l) => l.pathname.startsWith('/learn') && fromSource(l) === current.id,
      show: role !== null,
    },
    { to: `${base}/members`, label: 'Members', icon: Users, show: isAdminView },
    {
      to: `${base}/feedback`,
      label: 'Feedback',
      icon: MessageSquareHeart,
      show: isAdminView,
      badge: counts.data?.org?.feedback,
    },
    {
      to: `${base}/help`,
      label: 'Helpdesk',
      icon: LifeBuoy,
      show: isAdminView,
      badge: counts.data?.org?.help,
    },
    { to: `${base}/groups`, label: 'Groups', icon: UsersRound, show: isAdminView },
    { to: `${base}/library`, label: 'Library', icon: Library, show: isAuthor(role) },
    {
      to: `${base}/assignments`,
      label: 'Assignments',
      icon: CalendarClock,
      show: canManage || role === null,
    },
    { to: `${base}/reports`, label: 'Reports', icon: BarChart3, show: canManage || role === null },
    { to: `${base}/invites`, label: 'Invite members', icon: UserPlus, show: canManage },
    { to: `${base}/children`, label: 'Child organizations', icon: Network, show: showChildren },
    { to: `${base}/profile`, label: 'Profile', icon: IdCard, show: isAdminView },
    { to: `${base}/settings`, label: 'Settings', icon: Settings, show: canManage },
  ];
  return { title: current.name, items: items.filter((i) => i.show) };
}

export function SidebarContent({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  /** Called after a link is followed (closes the phone drawer). */
  onNavigate?: () => void;
}) {
  const me = useMe();
  const sections = useOpenSection();
  const org = useOrgItems();
  const counts = useInboxCounts();
  const [feedback, setFeedback] = useState(false);
  const [help, setHelp] = useState(false);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <Link
        to="/home"
        onClick={onNavigate}
        className={`flex h-14 shrink-0 items-center gap-2 border-b border-border font-semibold ${collapsed ? 'justify-center' : 'px-4'}`}
      >
        <ReadyMark className="size-6 shrink-0 text-primary" />
        {!collapsed && <span>Nirvik Ready</span>}
      </Link>
      <div className="shrink-0 border-b border-border p-2">
        <OrgSwitcher collapsed={collapsed} onNavigate={onNavigate} />
      </div>
      <nav aria-label="Main" className="min-h-0 flex-1 space-y-3 overflow-y-auto p-2">
        <Section
          id="learning"
          title="My Learning"
          items={[
            ...LEARNING,
            { to: '/help', label: 'Support Requests', icon: Inbox, badge: counts.data?.myHelp },
          ]}
          collapsed={collapsed}
          sections={sections}
          onNavigate={onNavigate}
        />
        {org && (
          <Section
            id="org"
            title={org.title}
            items={org.items}
            collapsed={collapsed}
            sections={sections}
            onNavigate={onNavigate}
          />
        )}
        {me.data?.isPlatformAdmin && (
          <Section
            id="admin"
            title="Platform admin"
            items={ADMIN.map((i) =>
              i.to === '/admin/feedback'
                ? { ...i, badge: counts.data?.platform?.feedback }
                : i.to === '/admin/help'
                  ? { ...i, badge: counts.data?.platform?.help }
                  : i,
            )}
            collapsed={collapsed}
            sections={sections}
            onNavigate={onNavigate}
          />
        )}
      </nav>
      <div className="shrink-0 space-y-0.5 border-t border-border p-2">
        <NavLink
          to="/resources"
          onClick={onNavigate}
          title={collapsed ? 'Help & resources' : undefined}
          className={({ isActive }) =>
            `flex min-h-10 w-full items-center gap-3 rounded-lg text-sm font-medium hover:bg-muted hover:text-foreground ${isActive ? 'bg-muted text-foreground' : 'text-muted-foreground'} ${collapsed ? 'justify-center px-2' : 'px-3'}`
          }
        >
          <BookOpen className="size-5 shrink-0" aria-hidden />
          {!collapsed && 'Help & resources'}
        </NavLink>
        <button
          type="button"
          onClick={() => setHelp(true)}
          title={collapsed ? 'Support' : undefined}
          className={`flex min-h-10 w-full items-center gap-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground ${collapsed ? 'justify-center px-2' : 'px-3'}`}
        >
          <LifeBuoy className="size-5 shrink-0" aria-hidden />
          {!collapsed && 'Support'}
        </button>
        <button
          type="button"
          onClick={() => setFeedback(true)}
          title={collapsed ? 'Feedback' : undefined}
          className={`flex min-h-10 w-full items-center gap-3 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground ${collapsed ? 'justify-center px-2' : 'px-3'}`}
        >
          <MessageSquareHeart className="size-5 shrink-0" aria-hidden />
          {!collapsed && 'Feedback'}
        </button>
        <UserMenu collapsed={collapsed} onNavigate={onNavigate} />
      </div>
      {feedback && <FeedbackDialog onClose={() => setFeedback(false)} />}
      {help && <HelpDialog onClose={() => setHelp(false)} />}
    </div>
  );
}

/** Account, appearance and sign out, under the user's name. */
function UserMenu({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const { user, signOut } = useAuth();
  const me = useMe();
  const { resolved, toggle } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  const name = me.data?.displayName ?? null;
  const email = user?.email ?? '';
  const letter = (name ?? email).trim().charAt(0).toUpperCase() || '?';

  const item =
    'flex min-h-10 w-full items-center gap-2.5 rounded-lg px-2 text-left text-sm hover:bg-muted';
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        title={collapsed ? (name ?? email) : undefined}
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center gap-3 rounded-lg hover:bg-muted ${collapsed ? 'justify-center p-1.5' : 'px-2 py-1.5'}`}
      >
        <span
          aria-hidden
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-primary-strong"
        >
          {letter}
        </span>
        {!collapsed && (
          <span className="min-w-0 flex-1 text-left">
            <span className="block truncate text-sm font-medium">{name ?? 'Your account'}</span>
            <span className="block truncate text-xs text-muted-foreground">{email}</span>
          </span>
        )}
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute z-40 w-60 rounded-xl border border-border bg-card p-1.5 shadow-lg ${collapsed ? 'bottom-0 left-full ml-2' : 'bottom-full left-0 mb-1'}`}
        >
          <Link
            role="menuitem"
            to="/account"
            className={item}
            onClick={() => {
              close();
              onNavigate?.();
            }}
          >
            <UserCog className="size-4 text-muted-foreground" aria-hidden /> Account
          </Link>
          <Link
            role="menuitem"
            to="/account/security"
            className={item}
            onClick={() => {
              close();
              onNavigate?.();
            }}
          >
            <ShieldCheck className="size-4 text-muted-foreground" aria-hidden /> Sign-in &amp;
            security
          </Link>
          <button type="button" role="menuitem" className={item} onClick={toggle}>
            {resolved === 'dark' ? (
              <Sun className="size-4 text-muted-foreground" aria-hidden />
            ) : (
              <Moon className="size-4 text-muted-foreground" aria-hidden />
            )}
            {resolved === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            role="menuitem"
            className={`${item} text-destructive`}
            onClick={async () => {
              close();
              await signOut();
              queryClient.clear();
              navigate('/');
            }}
          >
            <LogOut className="size-4" aria-hidden /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
